import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Users, CheckCircle2, XCircle, Loader2, Mail, ShieldCheck, Clock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_UI = {
  pending: { label: "Pendiente", cls: "bg-[#FEF6E7] text-[#92600A] border-[#F5DFAE]", Icon: Clock },
  active: { label: "Activa", cls: "bg-[#EAF7EE] text-[#1E6B3A] border-[#BFE5CB]", Icon: CheckCircle2 },
  rejected: { label: "Rechazada", cls: "bg-[#FDECEA] text-[#B3261E] border-[#F5C6C0]", Icon: XCircle },
};

export const UsersPanel = () => {
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/admin/users`);
      setUsers(data.users || []);
    } catch {
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const approve = async (u) => {
    setBusy(u.id);
    try {
      const { data } = await axios.post(`${API}/auth/admin/users/${u.id}/approve`);
      if (data.email === "sent") toast.success(`${u.email} aprobado · email de aviso enviado ✓`);
      else if (data.email === "skipped") toast.warning(`${u.email} aprobado, pero el email NO se envió: falta configurar RESEND_API_KEY`);
      else toast.warning(`${u.email} aprobado, pero el email falló: ${data.email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aprobar.");
    } finally {
      setBusy("");
    }
  };

  const reject = async (u) => {
    setBusy(u.id);
    try {
      await axios.post(`${API}/auth/admin/users/${u.id}/reject`);
      toast(`Acceso de ${u.email} rechazado`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo rechazar.");
    } finally {
      setBusy("");
    }
  };

  const pending = (users || []).filter((u) => u.status === "pending");
  const rest = (users || []).filter((u) => u.status !== "pending");

  return (
    <section data-testid="users-panel" className="rounded-3xl bg-white border border-[#F0E2D2] overflow-hidden mb-8">
      <div className="flex items-center gap-2.5 px-5 py-4 bg-[#241E18]">
        <Users size={16} className="text-[#FFB81C]" />
        <p className="text-[13px] font-extrabold text-white">Usuarios y solicitudes de acceso</p>
        {pending.length > 0 && (
          <span data-testid="pending-count-badge" className="px-2.5 py-0.5 rounded-full bg-[#F26F21] text-white text-[11px] font-extrabold">
            {pending.length} pendiente{pending.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {users === null ? (
        <div className="p-8 flex items-center justify-center gap-2 text-[#8A7F73] text-[13px] font-bold">
          <Loader2 size={15} className="animate-spin" /> Cargando usuarios…
        </div>
      ) : users.length === 0 ? (
        <p className="p-6 text-[13px] font-semibold text-[#8A7F73]">Aún no hay solicitudes de registro.</p>
      ) : (
        <div className="divide-y divide-[#F5EDE3]">
          {[...pending, ...rest].map((u) => {
            const ui = STATUS_UI[u.status] || STATUS_UI.pending;
            return (
              <div key={u.id} data-testid={`user-row-${u.email}`} className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-extrabold text-[#241E18] truncate flex items-center gap-2">
                    {u.email}
                    {u.role === "admin" && (
                      <span className="px-2 py-0.5 rounded-md bg-[#241E18] text-[#FFB81C] text-[9.5px] font-extrabold inline-flex items-center gap-1">
                        <ShieldCheck size={10} /> ADMIN
                      </span>
                    )}
                  </p>
                  <p className="text-[11.5px] text-[#8A7F73] font-semibold">
                    {u.name || "—"} · registrado {u.created_at ? new Date(u.created_at).toLocaleString("es-ES") : ""}
                    {u.approval_email && ` · email: ${u.approval_email === "sent" ? "enviado ✓" : u.approval_email}`}
                  </p>
                </div>
                <span className={`px-2.5 py-1 rounded-md border text-[10.5px] font-extrabold inline-flex items-center gap-1 ${ui.cls}`}>
                  <ui.Icon size={11} /> {ui.label}
                </span>
                {u.role !== "admin" && (
                  <>
                    <button
                      data-testid={`user-approve-btn-${u.email}`}
                      onClick={() => approve(u)}
                      disabled={busy === u.id}
                      className="h-8 px-4 rounded-full bg-[#2FA45A] text-white text-[12px] font-extrabold hover:bg-[#268A4B] disabled:opacity-40 transition-colors inline-flex items-center gap-1.5"
                    >
                      {busy === u.id ? <Loader2 size={12} className="animate-spin" /> : u.status === "active" ? <Mail size={12} /> : <CheckCircle2 size={12} />}
                      {u.status === "active" ? "Reenviar email" : "Aprobar"}
                    </button>
                    {u.status !== "rejected" && (
                      <button
                        data-testid={`user-reject-btn-${u.email}`}
                        onClick={() => reject(u)}
                        disabled={busy === u.id}
                        className="h-8 px-4 rounded-full border border-[#E4574A]/40 text-[#D93025] text-[12px] font-bold hover:bg-[#E4574A]/10 disabled:opacity-40 transition-colors inline-flex items-center gap-1.5"
                      >
                        <XCircle size={12} /> Rechazar
                      </button>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default UsersPanel;
